// ./src/utils/appCache.ts

// ============================================
// Types
// ============================================

export interface AppCacheEntry<T> {
  /** The cached data */
  data: T;
  /** Timestamp when the data was cached (ms since epoch) */
  cachedAt: number;
  /** App version at time of caching */
  appVersion: string;
  /** Locale/language at time of caching */
  locale: string;
  /** User ID at time of caching (null for anonymous/shared data) */
  userId: string | null;
}

export interface AppCacheSetOptions {
  /** Locale to associate with this cache entry (default: "en") */
  locale?: string;
  /** User ID to associate with this cache entry (default: null) */
  userId?: string | null;
}

export interface AppCacheGetOptions {
  /** Max age in milliseconds. If the entry is older, it's treated as a miss. */
  maxAge?: number;
  /** Expected locale. If it doesn't match, it's treated as a miss. */
  locale?: string;
  /** Expected user ID. If it doesn't match, it's treated as a miss. */
  userId?: string | null;
}

export interface AppCacheGetResult<T> {
  /** The cached data */
  data: T;
  /** Metadata about the cache entry */
  meta: Omit<AppCacheEntry<T>, "data">;
}

// ============================================
// Constants
// ============================================

const CACHE_KEY_PREFIX = "carexp:";
const DEFAULT_MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours

// ============================================
// Helpers
// ============================================

// The app version stamped into every cache entry is injected by the host app
// (so this package stays free of any build-tool / env assumptions like Vite's
// import.meta.env). Call configureAppCache() once at startup; until then the
// version reads as "unknown".
let appVersionProvider: () => string = () => "unknown";

/**
 * Inject how the cache should resolve the current app version. Call once at
 * startup, before any cached reads/writes (e.g. in the app entrypoint).
 */
export const configureAppCache = (opts: { getAppVersion: () => string }): void => {
  appVersionProvider = opts.getAppVersion;
};

function getAppVersion(): string {
  try {
    return `v${appVersionProvider() || "unknown"}`;
  } catch {
    return "unknown";
  }
}

function buildStorageKey(key: string): string {
  return `${CACHE_KEY_PREFIX}${key}`;
}

/**
 * Safely read from localStorage.
 * Returns null on any error (incognito, quota, parse failure, etc.)
 */
function safeGetItem(storageKey: string): string | null {
  try {
    return localStorage.getItem(storageKey);
  } catch {
    return null;
  }
}

/**
 * Safely write to localStorage.
 * Returns false on any error (incognito, quota exceeded, etc.)
 */
function safeSetItem(storageKey: string, value: string): boolean {
  try {
    localStorage.setItem(storageKey, value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Safely remove from localStorage.
 */
function safeRemoveItem(storageKey: string): void {
  try {
    localStorage.removeItem(storageKey);
  } catch {
    // Silently ignore
  }
}

// ============================================
// AppCache
// ============================================

/**
 * A generic, safe localStorage cache with built-in support for:
 * - TTL (time-to-live / max age)
 * - App version validation (auto-invalidates on new builds)
 * - Locale awareness (auto-invalidates on language change)
 * - User awareness (auto-invalidates on user change)
 * - Graceful degradation (incognito, quota errors, SSR — all safe)
 *
 * Usage:
 * ```ts
 * // --- Shared/global data (e.g., a catalog — no userId) ---
 * AppCache.set("catalog", data, { locale: "en" });
 *
 * const result = AppCache.get<Catalog>("catalog", {
 *   locale: "en",
 * });
 * if (result) {
 *   console.log(result.data); // cached Catalog
 * }
 *
 * // --- User-specific data (e.g., vehicles) ---
 * AppCache.set("vehicles", vehicles, { locale: "en", userId: "abc" });
 *
 * const vehicles = AppCache.get<Vehicle[]>("vehicles", {
 *   locale: "en",
 *   userId: "abc",
 * });
 *
 * // --- Invalidation ---
 * AppCache.remove("catalog");         // remove a specific key
 * AppCache.invalidate();              // all carexp: entries
 * AppCache.invalidate("vehicles");    // only carexp:vehicles*
 * AppCache.invalidateUserData();      // all user-specific entries (on sign-out)
 * AppCache.invalidateByUser("abc");   // all entries for user "abc"
 * ```
 */
export const AppCache = {
  /**
   * Retrieve a cached entry.
   *
   * Returns `null` if:
   * - The key doesn't exist
   * - The data can't be parsed
   * - The app version doesn't match the current build
   * - The locale doesn't match (when `locale` option is provided)
   * - The userId doesn't match (when `userId` option is provided)
   * - The entry has exceeded `maxAge` (default: 24 hours)
   *
   * On version mismatch, the stale entry is automatically removed.
   */
  get<T>(key: string, options: AppCacheGetOptions = {}): AppCacheGetResult<T> | null {
    const { maxAge = DEFAULT_MAX_AGE, locale, userId } = options;
    const storageKey = buildStorageKey(key);

    const raw = safeGetItem(storageKey);
    if (!raw) return null;

    let entry: AppCacheEntry<T>;
    try {
      entry = JSON.parse(raw);
    } catch {
      // Corrupted entry — clean up
      safeRemoveItem(storageKey);
      return null;
    }

    // Validate structure
    if (!entry || typeof entry.cachedAt !== "number" || entry.data === undefined) {
      safeRemoveItem(storageKey);
      return null;
    }

    // App version check — stale build means stale data
    const currentVersion = getAppVersion();
    if (entry.appVersion !== currentVersion) {
      safeRemoveItem(storageKey);
      return null;
    }

    // Locale check (only when caller specifies expected locale)
    if (locale !== undefined && entry.locale !== locale) {
      safeRemoveItem(storageKey);
      return null;
    }

    // User ID check (only when caller specifies expected userId)
    if (userId !== undefined && entry.userId !== userId) {
      safeRemoveItem(storageKey);
      return null;
    }

    // TTL check
    if (maxAge > 0) {
      const age = Date.now() - entry.cachedAt;
      if (age > maxAge) {
        safeRemoveItem(storageKey);
        return null;
      }
    }

    return {
      data: entry.data,
      meta: {
        cachedAt: entry.cachedAt,
        appVersion: entry.appVersion,
        locale: entry.locale,
        userId: entry.userId,
      },
    };
  },

  /**
   * Store data in the cache.
   *
   * Silently fails if localStorage is unavailable or quota is exceeded.
   * Returns `true` if the data was successfully stored, `false` otherwise.
   */
  set<T>(key: string, data: T, options: AppCacheSetOptions = {}): boolean {
    const { locale = "en", userId = null } = options;
    const storageKey = buildStorageKey(key);

    const entry: AppCacheEntry<T> = {
      data,
      cachedAt: Date.now(),
      appVersion: getAppVersion(),
      locale,
      userId,
    };

    try {
      const serialized = JSON.stringify(entry);
      return safeSetItem(storageKey, serialized);
    } catch {
      // Serialization failure (circular refs, etc.)
      return false;
    }
  },

  /**
   * Remove a specific cache entry.
   */
  remove(key: string): void {
    safeRemoveItem(buildStorageKey(key));
  },

  /**
   * Invalidate (remove) cache entries.
   *
   * - No argument: removes ALL entries with the `carexp:` prefix
   * - With prefix: removes only entries matching `carexp:{prefix}`
   */
  invalidate(prefix?: string): void {
    try {
      const targetPrefix = prefix
        ? buildStorageKey(prefix)
        : CACHE_KEY_PREFIX;

      const keysToRemove: string[] = [];

      for (let i = 0; i < localStorage.length; i++) {
        const storageKey = localStorage.key(i);
        if (storageKey && storageKey.startsWith(targetPrefix)) {
          keysToRemove.push(storageKey);
        }
      }

      for (const storageKey of keysToRemove) {
        safeRemoveItem(storageKey);
      }
    } catch {
      // Silently ignore — localStorage may be unavailable
    }
  },

  /**
   * Remove all cache entries that belong to a specific user.
   * Shared/global entries (userId === null) are left untouched.
   *
   * @param userId - The user ID whose cache entries should be removed
   */
  invalidateByUser(userId: string): void {
    this._invalidateByPredicate(
      (entry) => entry.userId === userId
    );
  },

  /**
   * Remove all cache entries that have any non-null userId.
   * Shared/global entries (userId === null) are left untouched.
   *
   * Typically called on sign-out to clear all user-specific data
   * while preserving shared caches like a catalog.
   */
  invalidateUserData(): void {
    this._invalidateByPredicate(
      (entry) => entry.userId !== null
    );
  },

  /**
   * Check if a valid (non-expired) cache entry exists for the given key.
   * Same validation rules as `get` but without deserializing the full data.
   */
  has(key: string, options: AppCacheGetOptions = {}): boolean {
    return this.get(key, options) !== null;
  },

  /**
   * Internal helper: iterate all carexp: entries, parse their metadata,
   * and remove those matching the predicate.
   */
  _invalidateByPredicate(
    predicate: (entry: Pick<AppCacheEntry<unknown>, "userId" | "locale" | "appVersion">) => boolean
  ): void {
    try {
      const keysToRemove: string[] = [];

      for (let i = 0; i < localStorage.length; i++) {
        const storageKey = localStorage.key(i);
        if (!storageKey || !storageKey.startsWith(CACHE_KEY_PREFIX)) continue;

        const raw = safeGetItem(storageKey);
        if (!raw) continue;

        try {
          const entry = JSON.parse(raw);
          if (entry && predicate(entry)) {
            keysToRemove.push(storageKey);
          }
        } catch {
          // Can't parse — skip (don't remove, might not be ours)
        }
      }

      for (const storageKey of keysToRemove) {
        safeRemoveItem(storageKey);
      }
    } catch {
      // Silently ignore — localStorage may be unavailable
    }
  },
};